import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';

import { useAuth } from '@/contexts/auth-context';
import {
  useInAppUserSubscribeMutation,
  UserSubscriptionDocument,
  useSetExpoTokenMutation,
  useUpgradeInAppSubscriptionMutation,
} from '@/graphql';
import {
  RNMessageType,
  WebActionType,
} from '@/types/native-bridge';
import { LS_KEY_NATIVE_OS_NAME } from '@/utils/constants';
import { nativeMessageHandler } from '@/utils/native-message-handler';

const RnWebView = () => {
  const navigate = useNavigate();
  const [setExpoToken] = useSetExpoTokenMutation({ context: { errorHandling: 'notify' } });
  const [upgradeInAppSubscription] = useUpgradeInAppSubscriptionMutation({ context: { errorHandling: 'notify' } });
  const { isAuthorized } = useAuth();

  const [inAppUserSubscribe] = useInAppUserSubscribeMutation({ context: { errorHandling: 'notify' } });

  useEffect(() => {
    if (!isAuthorized) return;

    const unsubscribeExpoToken = nativeMessageHandler.subscribe(
      RNMessageType.RN_EXPO_TOKEN,
      async (payload) => {
        try {
          const { data } = await setExpoToken({
            variables: {
              expoToken: payload as string,
            },
          });
          const resToken = data?.setUserExpoToken?.result;
          if (resToken) {
            window?.ReactNativeWebView?.postMessage(
              JSON.stringify({
                type: WebActionType.SendExpoToken,
                payload: {
                  token: resToken,
                },
              })
            );
          }
        } catch (error) {
          console.error('SET_EXPO_TOKEN', error);
        }
      }
    );

    const unsubscribeError = nativeMessageHandler.subscribe(
      RNMessageType.RN_ERROR,
      (payload) => {
        datadogRum.addAction('react-native-error', payload as object);
      }
    );

    const unsubscribePlatformInfo = nativeMessageHandler.subscribe(
      RNMessageType.RN_PLATFORM_INFO,
      (payload) => {
        const platformPayload = payload as { osName?: string };
        if (platformPayload.osName) {
          localStorage.setItem(LS_KEY_NATIVE_OS_NAME, platformPayload.osName);
        }
      }
    );

    const unsubscribeRenew = nativeMessageHandler.subscribe(
      RNMessageType.RN_SUBSCRIPTION_RENEW_SUCCESS,
      async (payload) => {
        try {
          await inAppUserSubscribe({
            variables: {
              subscriptionObject: payload,
            },
            refetchQueries: [{ query: UserSubscriptionDocument }],
          });
        } catch (error) {
          console.error('RN_RENEW_ERROR', error);
        }
      }
    );

    const unsubscribeUpgrade = nativeMessageHandler.subscribe(
      RNMessageType.RN_SUBSCRIPTION_UPGRADE_SUCCESS,
      async (payload) => {
        try {
          const { data } = await upgradeInAppSubscription({
            variables: {
              subscriptionObject: payload,
            },
            refetchQueries: [{ query: UserSubscriptionDocument }],
          });
          if (data?.changeInAppSubscription?.inAppSubscription?.me?.isInAppPremium) {
            navigate('/my-profile/network', {
              state: { prevPage: '/home' },
            });
          }
        } catch (error) {
          console.error('UPGRADE_IN_APP_SUBSCRIPTION', error);
        }
      }
    );

    return () => {
      unsubscribeExpoToken();
      unsubscribeError();
      unsubscribePlatformInfo();
      unsubscribeRenew();
      unsubscribeUpgrade();
    };
  }, [setExpoToken, isAuthorized, navigate, upgradeInAppSubscription, inAppUserSubscribe]);

  return <div />;
};

export default RnWebView;
